import { useMemo, useState } from 'react';
import {
  ActionButton,
  Alert,
  Button,
  CircularProgress,
  DataTable,
  dataTableSx,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  HairlineCard,
  Icons,
  ListSubheader,
  MenuItem,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  statusTone,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  titleCellSx,
  Tooltip,
  Typography,
  useRowActionsColumn,
} from '@munifocus/ui';
import { AppointableCandidate, APPOINTMENT_AUTHORITIES } from '@munifocus/contracts-gov';
import { useTranslation } from 'react-i18next';
import type { AppointmentSummary } from '@munifocus/contracts-gov';
import {
  useAppointableUsers,
  useAppointments,
  useApproveAppointment,
  useRejectAppointment,
  useContracts,
  useCreateAppointment,
  useRevocationPlan,
  useRevokeAppointment,
} from '../../lib/administration-api';
import { useScope } from '../../providers/ScopeProvider';
import { useMutationToast } from '../../lib/mutation-toast';
import { useSubmitValidation } from '../../lib/useSubmitValidation';
import { DeclineDialog } from './DeclineDialog';
import { formatDate } from '../../lib/relative-time';
import { OfficerCertificationsDialog } from '../../components/certifications/OfficerCertificationsDialog';

/**
 * Officer appointments at this municipality.
 *
 * The two-step is the point: a contractor's administrator proposes somebody and
 * the row lands in `pending_council` granting nothing; council approves it and
 * it becomes real. The API decides which happened from the caller's permissions,
 * so this screen shows one button or the other rather than asking.
 */
export function AppointmentsPanel() {
  const { t, i18n } = useTranslation();
  const actionsColumn = useRowActionsColumn();
  const toast = useMutationToast();
  const { active, can } = useScope();
  const localGovernmentId = active?.kind === 'local_government' ? active.id : null;

  const appointments = useAppointments(localGovernmentId);
  const appointable = useAppointableUsers(localGovernmentId);

  /**
   * Candidates by employer, each group alphabetical inside itself.
   *
   * The municipality's own staff lead: appointing one of your own employees is
   * the common case, and a contractor's is the one that needs a live contract
   * behind it. Contractors follow in name order, so the grouping is stable as
   * companies come and go.
   *
   * Grouped on `kind` rather than on the displayed name — the label is
   * translated, and sorting French strings against an English constant is how
   * the municipal group would have quietly stopped leading.
   */
  const groupedCandidates = useMemo(() => {
    const municipalLabel = t('gov.admin.officers.municipalStaff');
    const groups = new Map<string, AppointableCandidate[]>();
    for (const person of appointable.data ?? []) {
      const label = person.kind === 'municipal' ? municipalLabel : person.source;
      const group = groups.get(label) ?? [];
      group.push(person);
      groups.set(label, group);
    }
    return [...groups.entries()]
      .map(([label, group]) => {
        group.sort((a, b) => a.displayName.localeCompare(b.displayName));
        return [label, group] as const;
      })
      .sort(([a, ga], [b, gb]) => {
        const aMunicipal = ga[0]?.kind === 'municipal';
        const bMunicipal = gb[0]?.kind === 'municipal';
        if (aMunicipal !== bMunicipal) return aMunicipal ? -1 : 1;
        return a.localeCompare(b);
      });
  }, [appointable.data, t]);
  const contracts = useContracts({ localGovernmentId: localGovernmentId ?? undefined });
  const create = useCreateAppointment();
  const approve = useApproveAppointment();
  const reject = useRejectAppointment();
  const [declining, setDeclining] = useState<AppointmentSummary | null>(null);
  const revoke = useRevokeAppointment();

  const [adding, setAdding] = useState(false);
  const [userId, setUserId] = useState('');
  const [contractId, setContractId] = useState('');
  const [badgeNumber, setBadgeNumber] = useState('');
  const [councilResolutionRef, setCouncilResolutionRef] = useState('');
  const [authorities, setAuthorities] = useState<string[]>(['LGA']);
  const vAdd = useSubmitValidation({
    userId: userId ? null : t('common.validation.choose'),
    authorities: authorities.length > 0 ? null : t('common.validation.choose'),
  });
  const closeAdding = () => {
    setAdding(false);
    vAdd.reset();
  };
  const [revoking, setRevoking] = useState<AppointmentSummary | null>(null);
  const [viewingCerts, setViewingCerts] = useState<AppointmentSummary | null>(null);

  const plan = useRevocationPlan(revoking?.id ?? null, localGovernmentId ?? '');

  /**
   * Resolves whether the action landed, so a dialog can stay open — with what
   * was typed still in it — when it did not. Closing and clearing after a failed
   * save threw the officer's input away behind an error toast.
   */
  const run = async (successKey: string, fn: () => Promise<unknown>): Promise<boolean> => {
    try {
      await fn();
      toast.success(successKey);
      return true;
    } catch (err) {
      toast.error(err, 'gov.admin.errors.actionFailed');
      return false;
    }
  };

  if (!localGovernmentId) {
    return (
      <HairlineCard sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {t('gov.admin.officers.pickMunicipality')}
        </Typography>
      </HairlineCard>
    );
  }

  const rows = appointments.data ?? [];
  const liveContracts = (contracts.data ?? []).filter((c) => c.status === 'active');

  return (
    <Stack spacing={2}>
      <DataTable
        rows={rows}
        getRowKey={(row) => row.id}
        isLoading={appointments.isLoading}
        emptyLabel={t('gov.admin.officers.empty')}
        pageSize={25}
        initialSortKey="name"
        search={{
          placeholder: t('gov.admin.officers.searchPlaceholder'),
          of: (row) =>
            `${row.userName} ${row.userEmail} ${row.badgeNumber} ${row.companyName ?? ''}`,
        }}
        action={
          // Either permission opens the dialog, and they are held by different
          // people: a contractor proposes, the clerk appoints. Gating on
          // `officer:propose` alone hid the button from the only account that
          // can actually make an appointment live.
          can('officer:propose') || can('officer:appoint') ? (
            <ActionButton
              label={
                can('officer:appoint')
                  ? t('gov.admin.officers.appoint')
                  : t('gov.admin.officers.propose')
              }
              icon={Icons.Plus}
              onClick={() => setAdding(true)}
            />
          ) : null
        }
        columns={[
          {
            key: 'name',
            label: t('gov.admin.officers.name'),
            primary: true,
            sortValue: (row) => row.userName,
            cell: (row) => (
              <Stack>
                <Typography variant="body2" sx={titleCellSx}>
                  {row.userName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.userEmail}
                </Typography>
              </Stack>
            ),
          },
          {
            key: 'via',
            label: t('gov.admin.officers.via'),
            sortValue: (row) => row.companyName ?? '',
            // Null company means a municipality's own employee — the shorter
            // authority chain, and worth showing as such.
            cell: (row) => row.companyName ?? t('gov.admin.officers.directEmployee'),
          },
          {
            // When the appointment took effect — the Clerk's first question
            // after "who", so it is in the default view, not the column picker.
            key: 'appointedOn',
            label: t('gov.admin.officers.appointedOn'),
            nowrap: true,
            sortValue: (row) => row.appointedOn ?? '',
            cell: (row) => formatDate(row.appointedOn, i18n.language),
          },
          {
            key: 'status',
            label: t('gov.admin.officers.status'),
            sortValue: (row) => row.status,
            cell: (row) => (
              <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                <StatusChip
                  status={statusTone.appointment[row.status]}
                  label={t(`gov.admin.officers.statuses.${row.status}`)}
                />
                {row.status === 'rejected' && row.rejectionReason && (
                  <Tooltip title={row.rejectionReason}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ maxWidth: 220 }}
                      noWrap
                    >
                      {row.rejectionReason}
                    </Typography>
                  </Tooltip>
                )}
              </Stack>
            ),
          },
          {
            key: 'authorities',
            label: t('gov.admin.officers.authorities'),
            optional: true,
            sortValue: (row) => row.authorities.join(', '),
            cell: (row) => row.authorities.join(', ') || '—',
          },
          {
            key: 'badge',
            label: t('gov.admin.officers.badge'),
            mono: true,
            nowrap: true,
            sortValue: (row) => row.badgeNumber,
            cell: (row) => row.badgeNumber || '—',
          },
          {
            key: 'openFiles',
            label: t('gov.admin.officers.openFiles'),
            align: 'right',
            nowrap: true,
            sortValue: (row) => row.openFileCount,
            cell: (row) => row.openFileCount,
          },
          {
            ...actionsColumn,
            cell: (row) => (
              <RowActions>
                {row.status === 'active' && (
                  <RowAction
                    label={t('gov.certifications.view')}
                    icon={Icons.Award}
                    onClick={() => setViewingCerts(row)}
                  />
                )}
                {can('officer:appoint') && row.status === 'pending_council' && (
                  <RowAction
                    label={t('gov.admin.officers.approve')}
                    icon={Icons.Check}
                    onClick={() =>
                      run('gov.admin.officers.approved', () =>
                        approve.mutateAsync({ id: row.id, localGovernmentId }),
                      )
                    }
                  />
                )}
                {can('officer:appoint') && row.status === 'pending_council' && (
                  <RowAction
                    label={t('gov.admin.decline.action')}
                    icon={Icons.X}
                    tone="danger"
                    onClick={() => setDeclining(row)}
                  />
                )}
                {can('officer:revoke') && row.status === 'active' && (
                  <RowAction
                    label={t('gov.admin.officers.revoke')}
                    icon={Icons.Trash2}
                    tone="danger"
                    onClick={() => setRevoking(row)}
                  />
                )}
              </RowActions>
            ),
          },
        ]}
      />

      <DeclineDialog
        open={Boolean(declining)}
        title={t('gov.admin.officers.declineTitle')}
        help={t('gov.admin.officers.declineHelp', {
          name: declining?.userName ?? '',
          company: declining?.companyName ?? '',
        })}
        pending={reject.isPending}
        onClose={() => setDeclining(null)}
        onConfirm={(reason) =>
          declining
            ? run('gov.admin.officers.declined', () =>
                reject.mutateAsync({ id: declining.id, reason }),
              )
            : Promise.resolve(false)
        }
      />

      <Dialog open={adding} onClose={closeAdding} fullWidth maxWidth="sm">
        <DialogTitle>
          {can('officer:appoint')
            ? t('gov.admin.officers.appoint')
            : t('gov.admin.officers.propose')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {!can('officer:appoint') && (
              <Alert severity="info">{t('gov.admin.officers.proposeHelp')}</Alert>
            )}
            <TextField
              select
              label={t('gov.admin.officers.person')}
              {...vAdd.field('userId')}
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              fullWidth
              required
              helperText={vAdd.errors.userId ?? t('gov.admin.officers.personHelp')}
            >
              {/* Grouped by where each person comes from, not run together
                  alphabetically. With three candidates the flat list was
                  readable; with thirty it is a wall in which a municipality's
                  own staff and four contractors' employees are indistinguishable
                  — and which organisation someone belongs to is the first thing
                  a clerk needs to know before appointing them. */}
              {groupedCandidates.flatMap(([source, people]) => [
                <ListSubheader key={`h:${source}`}>{source}</ListSubheader>,
                ...people.map((person) => (
                  <MenuItem key={person.id} value={person.id}>
                    {person.displayName}
                  </MenuItem>
                )),
              ])}
            </TextField>
            <TextField
              select
              label={t('gov.admin.officers.contract')}
              value={contractId}
              onChange={(e) => setContractId(e.target.value)}
              fullWidth
              helperText={t('gov.admin.officers.contractHelp')}
            >
              <MenuItem value="">{t('gov.admin.officers.directEmployee')}</MenuItem>
              {liveContracts.map((contract) => (
                <MenuItem key={contract.id} value={contract.id}>
                  {contract.companyName} — {contract.reference || contract.id.slice(0, 8)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              slotProps={{ select: { multiple: true } }}
              label={t('gov.admin.officers.authorities')}
              {...vAdd.field('authorities')}
              value={authorities}
              onChange={(e) =>
                setAuthorities(
                  typeof e.target.value === 'string'
                    ? e.target.value.split(',')
                    : (e.target.value as unknown as string[]),
                )
              }
              fullWidth
              required
            >
              {APPOINTMENT_AUTHORITIES.map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('gov.admin.officers.badge')}
              value={badgeNumber}
              onChange={(e) => setBadgeNumber(e.target.value)}
              fullWidth
            />
            {/* Council authorises the appointment; the clerk records it. This
                is where that authority is named. Only shown to the account that
                can make an appointment live — a contractor is proposing, and
                has no resolution to cite yet. */}
            {can('officer:appoint') && (
              <TextField
                label={t('gov.admin.officers.resolutionRef')}
                helperText={t('gov.admin.officers.resolutionHelp')}
                value={councilResolutionRef}
                onChange={(e) => setCouncilResolutionRef(e.target.value)}
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdding}>{t('common.actions.cancel')}</Button>
          <Button
            variant="contained"
            disabled={create.isPending}
            onClick={vAdd.guard(async () => {
              const ok = await run('gov.admin.officers.created', () =>
                create.mutateAsync({
                  userId,
                  localGovernmentId,
                  companyContractId: contractId || undefined,
                  authorities: authorities as never,
                  badgeNumber,
                  councilResolutionRef: councilResolutionRef || undefined,
                }),
              );
              if (!ok) return;
              closeAdding();
              setUserId('');
              setContractId('');
              setBadgeNumber('');
              setCouncilResolutionRef('');
            })}
          >
            {t('common.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revocation is refused while it would orphan a live file, so the plan
          is shown before the button is offered. */}
      <OfficerCertificationsDialog
        localGovernmentId={localGovernmentId}
        officer={viewingCerts ? { userId: viewingCerts.userId, name: viewingCerts.userName } : null}
        onClose={() => setViewingCerts(null)}
      />

      <Dialog open={Boolean(revoking)} onClose={() => setRevoking(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('gov.admin.officers.revokeTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('gov.admin.officers.revokeHelp', { name: revoking?.userName ?? '' })}
            </Typography>
            {plan.isLoading && <CircularProgress size={20} />}
            {plan.data && (
              <Alert severity={plan.data.canProceed ? 'warning' : 'error'}>
                {plan.data.files.length === 0
                  ? t('gov.admin.revocation.noOpenFiles')
                  : plan.data.canProceed
                    ? t('gov.admin.revocation.willTransfer', { count: plan.data.files.length })
                    : t('gov.admin.revocation.blocked', { count: plan.data.unresolvableCount })}
              </Alert>
            )}
            {plan.data && plan.data.files.length > 0 && (
              // The same header rhythm and row height as every other table in the
              // portal. A dialog is not a reason for a table to look different.
              <Table size="small" sx={dataTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('gov.admin.revocation.file')}</TableCell>
                    <TableCell>{t('gov.admin.revocation.to')}</TableCell>
                    <TableCell>{t('gov.admin.revocation.basis')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.data.files.map((file) => (
                    <TableRow key={file.fileId}>
                      <TableCell>{file.fileReference}</TableCell>
                      <TableCell>
                        {file.proposedSuccessorName ?? (
                          <Typography variant="body2" color="error">
                            {t('gov.admin.revocation.noSuccessor')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {t(`gov.admin.revocation.bases.${file.basis}`)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevoking(null)}>{t('common.actions.cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={revoke.isPending || plan.data?.canProceed === false}
            onClick={async () => {
              if (!revoking) return;
              const ok = await run('gov.admin.officers.revoked', () =>
                revoke.mutateAsync({ id: revoking.id }),
              );
              if (!ok) return;
              setRevoking(null);
            }}
          >
            {t('gov.admin.officers.revoke')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
